@if (isOpen()) {
  <div
    class="ctx-backdrop fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-4"
    role="dialog"
    aria-modal="true"
    [attr.aria-labelledby]="titleId"
  >
    <div
      #panel
      class="ctx-panel relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl flex flex-col max-h-[92dvh] sm:max-h-[88vh] overflow-hidden"
    >
      <!-- Signature accent rail -->
      <div class="h-1 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-400 flex-shrink-0"></div>

      <div class="sm:hidden mx-auto mt-3 h-1 w-10 rounded-full bg-gray-300 flex-shrink-0"></div>

      <!-- Header -->
      <div class="px-4 sm:px-6 pt-4 sm:pt-5 pb-4 border-b border-gray-100">
        <div class="flex items-start gap-4">
          <div class="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm shadow-blue-600/20">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h3 [id]="titleId" class="text-lg font-semibold text-gray-900 leading-snug">
              @if (forceBlock()) {
                Select your workspace
              } @else {
                Switch workspace
              }
            </h3>
            <p class="mt-0.5 text-sm text-gray-500">
              @if (canSwitchTenant()) {
                Choose a village and committee to work in.
              } @else {
                Pick the committee you want to work in.
              }
            </p>
          </div>

          @if (!forceBlock()) {
            <button
              type="button"
              (click)="cancel()"
              [disabled]="isApplying()"
              class="flex-shrink-0 p-1.5 -m-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
              aria-label="Close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          }
        </div>

        <!-- Current workspace -->
        @if (hasCurrentContext() && !forceBlock()) {
          <div class="mt-4 flex items-center gap-2.5 rounded-xl bg-gray-50 border border-gray-100 px-3 py-2">
            <span class="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-600">
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              Working in
            </span>
            <span class="min-w-0 flex items-center gap-1.5 text-sm text-gray-700">
              <span class="font-semibold text-gray-900 truncate">{{ currentTenantName() }}</span>
              <svg class="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
              <span class="font-medium text-gray-600 truncate">{{ currentBuName() }}</span>
            </span>
          </div>
        }
      </div>

      <!-- Body -->
      <div class="ctx-body flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-5 relative">

        <!-- Tenant picker (SuperAdmin only) -->
        @if (canSwitchTenant()) {
          <div class="mb-5 relative" data-village-combobox>
            <label class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              Village
            </label>
            <div class="relative">
              <input
                #villageInput
                data-initial-focus
                type="text"
                role="combobox"
                autocomplete="off"
                aria-controls="village-listbox"
                [attr.aria-expanded]="villageDropdownOpen()"
                aria-autocomplete="list"
                [value]="villageInputDisplay()"
                (input)="onVillageInput($event)"
                (focus)="openVillageDropdown()"
                (click)="onVillageInputClick()"
                (keydown)="onVillageInputKeydown($event)"
                [disabled]="isApplying() || tenantOptions().length === 0"
                [placeholder]="tenantOptions().length === 0 ? 'No villages available' : 'Search village…'"
                class="w-full border border-gray-300 rounded-xl bg-white pl-3.5 pr-10 py-2.5 text-sm font-medium text-gray-900 shadow-sm transition focus:outline-none focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500 disabled:bg-gray-50 disabled:opacity-60"
              />
              <svg class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform"
                   [class.rotate-180]="villageDropdownOpen()"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>

            @if (villageDropdownOpen()) {
              <ul
                id="village-listbox"
                role="listbox"
                class="ctx-pop absolute z-20 mt-1.5 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-900/10 p-1"
              >
                @if (filteredVillages().length === 0) {
                  <li class="px-3 py-2.5 text-sm text-gray-500">No villages match “{{ villageSearch() }}”.</li>
                }
                @for (t of filteredVillages(); track t.tenantId; let i = $index) {
                  <li
                    role="option"
                    [attr.aria-selected]="draftTenantId() === t.tenantId"
                    (mousedown)="$event.preventDefault(); pickVillage(t.tenantId, t.villageName)"
                    (mouseenter)="villageHighlightIndex.set(i)"
                    class="px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-2 rounded-lg"
                    [class.bg-blue-50]="villageHighlightIndex() === i"
                  >
                    <span class="truncate" [class.font-semibold]="draftTenantId() === t.tenantId"
                          [class.text-blue-700]="draftTenantId() === t.tenantId">{{ t.villageName }}</span>
                    @if (draftTenantId() === t.tenantId) {
                      <svg class="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        }

        <!-- BU section -->
        <div>
          <div class="flex items-center justify-between gap-3 mb-2.5">
            <label class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
              <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              Committee
              @if (!previewLoading() && visibleBus().length > 0) {
                <span class="text-gray-300 font-normal normal-case tracking-normal">·&nbsp;{{ visibleBus().length }}</span>
              }
            </label>
            @if (visibleBus().length > 0) {
              <div class="relative">
                <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                  #searchInput
                  type="text"
                  [ngModel]="search()"
                  (ngModelChange)="search.set($event)"
                  placeholder="Search…"
                  class="w-40 sm:w-48 pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500 transition"
                />
              </div>
            }
          </div>

          @if (previewLoading()) {
            <ul class="grid grid-cols-1 sm:grid-cols-2 gap-3" aria-hidden="true">
              @for (s of [1,2,3,4]; track s) {
                <li class="rounded-xl p-4 border border-gray-100 bg-white">
                  <div class="flex items-start gap-3">
                    <div class="ctx-skeleton w-9 h-9 rounded-lg flex-shrink-0"></div>
                    <div class="flex-1 min-w-0 space-y-2 pt-0.5">
                      <div class="ctx-skeleton h-3 rounded w-2/3"></div>
                      <div class="ctx-skeleton h-2.5 rounded w-full"></div>
                    </div>
                  </div>
                </li>
              }
            </ul>
          } @else if (filteredBus().length === 0) {
            <div class="py-12 text-center">
              <div class="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <p class="text-sm font-medium text-gray-700">
                @if (search()) { No committees match “{{ search() }}”. }
                @else { No committees available in this village. }
              </p>
              @if (forceBlock() && !search()) {
                <p class="mt-1 text-xs text-gray-500">Contact your administrator to be assigned.</p>
              }
            </div>
          } @else {
            <ul class="grid grid-cols-1 sm:grid-cols-2 gap-3" role="listbox" [attr.aria-label]="'Committees'">
              @for (bu of filteredBus(); track trackBu($index, bu)) {
                <li>
                  <button
                    type="button"
                    role="option"
                    [attr.aria-selected]="isBuSelected(bu.id)"
                    (click)="selectBu(bu.id)"
                    [disabled]="isApplying()"
                    class="ctx-card group w-full text-left bg-white rounded-xl p-4 border transition-all duration-150 focus:outline-none focus:ring-4 focus:ring-blue-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                    [class.border-blue-500]="isBuSelected(bu.id)"
                    [class.ring-2]="isBuSelected(bu.id)"
                    [class.ring-blue-500]="isBuSelected(bu.id)"
                    [class.bg-blue-50]="isBuSelected(bu.id)"
                    [class.shadow-sm]="!isBuSelected(bu.id)"
                    [class.border-gray-200]="!isBuSelected(bu.id)"
                    [class.hover:border-blue-300]="!isBuSelected(bu.id)"
                    [class.hover:shadow-md]="!isBuSelected(bu.id)"
                  >
                    <div class="flex items-start gap-3">
                      <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-semibold transition-colors"
                           [class.bg-blue-600]="isBuSelected(bu.id)"
                           [class.text-white]="isBuSelected(bu.id)"
                           [class.bg-gray-100]="!isBuSelected(bu.id)"
                           [class.text-gray-500]="!isBuSelected(bu.id)">
                        {{ (bu.name || '?').charAt(0).toUpperCase() }}
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold text-gray-900 truncate">{{ bu.name }}</p>
                        @if (bu.description) {
                          <p class="mt-0.5 text-xs text-gray-500 line-clamp-2">{{ bu.description }}</p>
                        }
                      </div>
                      <span class="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition"
                            [class.bg-blue-600]="isBuSelected(bu.id)"
                            [class.border]="!isBuSelected(bu.id)"
                            [class.border-gray-300]="!isBuSelected(bu.id)">
                        @if (isBuSelected(bu.id)) {
                          <svg class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                          </svg>
                        }
                      </span>
                    </div>
                  </button>
                </li>
              }
            </ul>
          }
        </div>

        @if (errorMessage()) {
          <div class="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
            <div class="flex-1">
              <p>{{ errorMessage() }}</p>
              @if (applyStep() === 'bu') {
                <button type="button" (click)="retryBu()" [disabled]="isApplying()"
                        class="mt-2 text-xs font-semibold underline hover:text-red-900">
                  Retry committee switch
                </button>
              }
            </div>
          </div>
        }

        <!-- Busy overlay: masks the body during a switch so no transient reset/empty
             state is ever visible (e.g. the committee grid re-rendering between the
             village-token and committee-token steps). -->
        @if (isApplying()) {
          <div class="ctx-busy absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-sm text-center px-6">
            <app-spinner size="sm" colour="muted"></app-spinner>
            <p class="text-sm font-medium text-gray-600">
              @if (applyStep() === 'tenant') { Switching village… }
              @else if (applyStep() === 'bu') { Switching committee… }
              @else { Switching workspace… }
            </p>
          </div>
        }
      </div>

      <!-- Footer -->
      <div class="px-4 sm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between gap-3">
        <div class="text-xs text-gray-500 hidden sm:block">
          @if (forceBlock()) {
            <span class="inline-flex items-center gap-1.5">
              <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
              Required to continue
            </span>
          } @else if (draftBuName()) {
            <span>Switching to <span class="font-medium text-gray-700">{{ draftBuName() }}</span></span>
          }
        </div>

        <div class="flex items-center gap-2 ml-auto max-sm:w-full max-sm:grid max-sm:grid-cols-2">
          @if (forceBlock()) {
            <button
              type="button"
              (click)="logoutFromModal()"
              [disabled]="isApplying()"
              class="px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            >
              Sign out
            </button>
          } @else {
            <button
              type="button"
              (click)="cancel()"
              [disabled]="isApplying()"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
          }
          <button
            type="button"
            (click)="apply()"
            [disabled]="!canApply()"
            class="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            @if (isApplying()) {
              <app-spinner size="xs" colour="white"></app-spinner>
            }
            @if (isApplying() && applyStep() === 'tenant') {
              Switching village…
            } @else if (isApplying() && applyStep() === 'bu') {
              Switching committee…
            } @else {
              Switch workspace
            }
          </button>
        </div>
      </div>
    </div>
  </div>
}
